import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { API_BASE_URL } from "../../constants";
import { parseApiError } from "../../services/apiError";
import CopyField from "./CopyField";
import {
  envFileContents,
  envFileName,
  hostedServerName,
  mcpAddCommand,
  mcpAddHostedCommand,
} from "./agentSetup";

/**
 * Mint an agent, or show a freshly rotated token.
 *
 * Two screens. The form (name, description) calls onMint and, on success,
 * switches to the token screen: the token, the env-file line, and the
 * `claude mcp add` command, each with Copy. That screen can only be left
 * through "I've saved it" — the token is never shown again.
 *
 * Pass `minted` ({username, token}) to open directly on the token screen
 * (used after Rotate).
 */
function MintAgentModal({
  show,
  onHide,
  onMint,
  minted = null,
  returnFocusTo = null,
}) {
  const [username, setUsername] = useState("");
  const [description, setDescription] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const usernameRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    setUsername("");
    setDescription("");
    setFieldErrors({});
    setFormError(null);
    setSaving(false);
    setResult(minted);
  }, [show, minted]);

  // Belt: forget the token once this isn't shown, in case a caller only
  // flips `show` — the parent's onHide already clears its own copy
  // synchronously (the old modal's onExited did the same forgetting once the
  // modal's fade-out finished).
  useEffect(() => {
    if (!show) setResult(null);
  }, [show]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const data = await onMint({
        username: username.trim(),
        description: description.trim(),
      });
      setResult(data);
    } catch (err) {
      const { formError: banner, fieldErrors: fields } = parseApiError(
        err,
        "Failed to mint the agent",
      );
      setFieldErrors(fields);
      setFormError(banner);
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (name) => {
    const err = fieldErrors[name];
    return err ? [].concat(err).join(" ") : null;
  };

  const onTokenScreen = Boolean(result);
  const busy = saving || onTokenScreen;

  return (
    <Dialog open={show} onOpenChange={(open) => !open && !busy && onHide()}>
      <DialogContent
        showCloseButton={!busy}
        className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => {
          // The opener may have been disabled (busyAny) when this opened,
          // so ui/dialog.jsx captured <body>; the list passes the real one.
          if (returnFocusTo?.current) {
            event.preventDefault();
            returnFocusTo.current.focus();
          }
        }}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        onOpenAutoFocus={(event) => {
          if (onTokenScreen) return;
          event.preventDefault();
          usernameRef.current?.focus();
        }}
      >
        {onTokenScreen ? (
          <>
            <DialogHeader className="border-b px-6 py-4 text-left">
              <DialogTitle className="font-display text-xl">
                {minted
                  ? `New token for ${result.username}`
                  : `${result.username} is ready`}
              </DialogTitle>
              <DialogDescription className="sr-only">
                The agent&apos;s token and setup instructions, shown once.
              </DialogDescription>
            </DialogHeader>
            <div
              data-slot="dialog-body"
              className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
            >
              <Alert className="mb-4">
                <AlertDescription>
                  <strong>This is the only time the token is shown.</strong>{" "}
                  Copy it now; if it&apos;s lost, rotate it for a new one.
                </AlertDescription>
              </Alert>
              <CopyField id="agent-token" label="Token" value={result.token} />
              <CopyField
                id="agent-env"
                label={`Save as mcp_server/${envFileName(result.username)}`}
                value={envFileContents(result.token, API_BASE_URL)}
                multiline
              />
              <CopyField
                id="agent-mcp"
                label="Then add the MCP server entry (Claude Code, from a clone of this repo)"
                value={mcpAddCommand(result.username)}
                multiline
              />
              {/* The hosted MCP (projects/mcp-hosting): same tools, nothing to
                  clone or run. The token travels as a header and lives in
                  Claude Code's user config — the env file above is then not
                  needed at all. Two named modes, one screen. This line
                  CONTAINS the token (the stdio one doesn't), so say where it
                  lands. */}
              <CopyField
                id="agent-mcp-hosted"
                label="Or, from any machine — the hosted MCP (no clone, no env file)"
                value={mcpAddHostedCommand(
                  hostedServerName(result.username),
                  result.token,
                  API_BASE_URL,
                )}
                multiline
              />
              <p className="text-sm text-muted-foreground">
                That hosted line contains the token: pasting it writes the token
                to your shell history and to <code>~/.claude.json</code>. Run it
                from the agent&apos;s own account, or lead with a space so your
                shell skips history.
                {minted && (
                  <>
                    {" "}
                    Rotated? Remove the old entry first:{" "}
                    <code>
                      claude mcp remove {hostedServerName(result.username)} -s
                      user
                    </code>
                    .
                  </>
                )}
              </p>
              {/* This screen used to end "it can see household projects and
                  nothing private", which stopped being true when
                  projects/shared-library made UserProfile.owner a visibility
                  switch: mint fills an empty owner from the minter (or the
                  sole staff account), so the token reads a whole library from
                  the moment this dialog opens. The grant happens HERE — say so
                  here (close-out review). */}
              {result.owner ? (
                <Alert className="mb-2">
                  <AlertDescription>
                    <strong>
                      This token reads {result.owner.username}&apos;s library.
                    </strong>{" "}
                    {result.username} was given {result.owner.username} as its
                    owner, so it can read and write every document, tag and
                    snippet they own — not just what it creates itself. Change
                    or clear the owner on the Agents list if that is not what
                    you want.
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="mb-2">
                  <AlertDescription>
                    <strong>{result.username} has no library yet.</strong> It
                    sees only its own rows, and anything it writes is invisible
                    to everyone else until you give it an owner on the Agents
                    list.
                  </AlertDescription>
                </Alert>
              )}
              <p className="mb-0 text-sm text-muted-foreground">
                In a new session, <code>whoami</code> on the{" "}
                <code>mono-{result.username}</code> (or{" "}
                <code>{hostedServerName(result.username)}</code>) server should
                answer as the agent. It can also see household projects.
              </p>
            </div>
            <DialogFooter className="border-t px-6 py-4">
              <Button onClick={onHide}>I&apos;ve saved it</Button>
            </DialogFooter>
          </>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <DialogHeader className="border-b px-6 py-4 text-left">
              <DialogTitle className="font-display text-xl">
                New agent
              </DialogTitle>
              <DialogDescription className="sr-only">
                Mint a new agent
              </DialogDescription>
            </DialogHeader>
            <div
              data-slot="dialog-body"
              className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
            >
              {formError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}
              <div className="mb-4 grid gap-1.5">
                <Label htmlFor="agent-username">Name</Label>
                <Input
                  id="agent-username"
                  ref={usernameRef}
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  aria-invalid={Boolean(fieldError("username")) || undefined}
                  aria-describedby={
                    fieldError("username") ? "agent-username-error" : undefined
                  }
                  placeholder="scout"
                  required
                />
                {fieldError("username") ? (
                  <p
                    id="agent-username-error"
                    className="text-sm text-destructive"
                  >
                    {fieldError("username")}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Letters, digits and @/./+/-/_ — it becomes the agent&apos;s
                    username and its MCP server name (<code>mono-scout</code>).
                  </p>
                )}
              </div>
              <div className="mb-1 grid gap-1.5">
                <Label htmlFor="agent-description">What it&apos;s for</Label>
                <Textarea
                  id="agent-description"
                  className="max-h-48"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Works the yard tasks on weekday mornings"
                />
                <p className="text-sm text-muted-foreground">
                  Optional. Shown on the Agents page so the household knows what
                  this one does.
                </p>
              </div>
            </div>
            <DialogFooter className="border-t px-6 py-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onHide}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !username.trim()}>
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Minting…
                  </>
                ) : (
                  "Mint agent"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

MintAgentModal.propTypes = {
  returnFocusTo: PropTypes.shape({ current: PropTypes.any }),
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onMint: PropTypes.func.isRequired,
  minted: PropTypes.shape({
    username: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
  }),
};

export default MintAgentModal;
